<template>
    <figure
        v-if="service"
        class="video-embed"
    >
        <div
            v-if="$slots.placeholder && !requestedPlayback"
            class="video-embed__placeholder"
            aria-label="Press ENTER to play video"
            tabindex="0"
            @click="requestedPlayback = true"
            @keydown.enter="requestedPlayback = true"
        >
            <slot name="placeholder" />
        </div>
        <div
            v-else
            class="video-embed__aspect-ratio"
        >
            <div class="video-embed__loading-spinner" />
            <template v-if="service === SERVICE_YOUTUBE">
                <iframe
                    class="video-embed__item"
                    :src="`https://www.youtube-nocookie.com/embed/${id}?modestbranding=1&controls=1&rel=0${autoplaySettings}`"
                    frameborder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                    :loading="loading"
                />
            </template>

            <template v-if="service === SERVICE_VIMEO">
                <iframe
                    class="video-embed__item"
                    :src="`https://player.vimeo.com/video/${id}?byline=0&portrait=0&color=ffffff&dnt=1&pip=0&responsive=1&title=0&byline=0&transparent=0${autoplaySettings}`"
                    frameborder="0"
                    allow="autoplay; fullscreen"
                    allowfullscreen
                    :loading="loading"
                />
            </template>
        </div>

        <figcaption v-if="caption || $slots.caption">
            <slot name="caption">{{ caption }}</slot>
        </figcaption>
    </figure>
</template>

<script setup lang="ts">
import { ref, computed, withDefaults } from 'vue';

const requestedPlayback = ref(false);

const SERVICE_YOUTUBE = 'youtube',
    SERVICE_VIMEO = 'vimeo',
    YTREGEX = /^(?:(?:https?:)?\/\/)?(?:(?:www|m)\.)?(?:youtube\.com|youtu.be)(?:\/(?:(?:.+)[?&]v=|embed\/|v\/)?)([\w-]+)(?:\S+)?$/i,
    VIMEOREGEX = /^https:\/\/vimeo\.com\/([0-9]+)$/i;

interface Props {
  url: string;
  caption?: string;
  loading?: string;
  autoplay?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    url: '',
    caption: '',
    loading: 'lazy',
    autoplay: false
});

// Parse the URL into a service and ID
const parsed = computed(() => {
    const url = props.url;
    // Match the URL to a predefined list of services
    const serviceName = url.match(YTREGEX) ? SERVICE_YOUTUBE : url.match(VIMEOREGEX) ? SERVICE_VIMEO : null;

    let parsedId = null;
    // Using predefined REGEXs for URL patterns, attempt to parse out just the video ID
    if (serviceName) {
        const regex = serviceName === SERVICE_YOUTUBE ? YTREGEX : VIMEOREGEX;
        const matches = url.match(regex);
        parsedId = matches ? matches[1] : null;
    }

    // Return a summary of service + ID
    return {
        service: serviceName,
        id: parsedId
    };
});

// Wrap the service and ID in computed properties for convenience
const service = computed(() => parsed.value.service);
const id = computed(() => parsed.value.id);
const autoplaySettings = computed(() => props.autoplay ? '&autoplay=1' : '');
</script>

<style lang="less" src="./video-embed.less"></style>
